import { AsyncPipe, LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxColor } from '@onyx/angular';
import {
  OnyxDatePipe,
  OnyxDotComponent,
  OnyxIconComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { DateTime } from 'luxon';
import { OrderPointStatus } from '../../../../../../../dashboard/orders/common/enums/order-point-status';
import { DictionaryCode } from '../../../../../../enums/dictionary-code';
import { CommonHelper } from '../../../../../../helpers/common.helper';
import { RouteHelper } from '../../../../../../helpers/route.helper';
import { RoutePointValueHelper } from '../../../../../../helpers/route-point-value.helper';
import { DiffPipe } from '../../../../../../pipes/diff.pipe';
import { DurationPipe } from '../../../../../../pipes/duration.pipe';
import type { RoutePoint } from '../../../../../../services/route.service';
import { RouteService } from '../../../../../../services/route.service';

@Component({
  selector: 'app-route-point-details-status-in-progress',
  imports: [
    AsyncPipe,
    LowerCasePipe,
    NgTemplateOutlet,
    TranslatePipe,
    OnyxDotComponent,
    OnyxIconComponent,
    OnyxTooltipDirective,
    OnyxDatePipe,
    DiffPipe,
    DurationPipe,
  ],
  templateUrl: './route-point-details-status-in-progress.component.html',
  styleUrl: './route-point-details-status-in-progress.component.scss',
  host: {
    '[class]': `context`,
  },
})
export class RoutePointDetailsStatusInProgressComponent {
  private readonly routeService = inject(RouteService);
  private readonly commonHelper = inject(CommonHelper);

  public readonly point = input.required<RoutePoint>();
  public readonly isCanceled = input.required<boolean>();

  protected readonly I18N = 'route.details.status';

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly OrderPointStatus = OrderPointStatus;

  protected readonly context = this.routeService.context;

  protected value = computed(() =>
    RoutePointValueHelper.getValue(this.point()),
  );
  protected timestamps = computed(() =>
    RouteHelper.getTimestamps(this.point()),
  );
  protected driversStatuses = computed(() =>
    RouteHelper.getDriverReportedStatuses(this.point()),
  );
  protected status = computed(() => RouteHelper.getStatus(this.point()));
  protected isToDo = computed(() => this.status() === OrderPointStatus.TO_DO);
  protected isInPoint = computed(() =>
    [OrderPointStatus.WAITING, OrderPointStatus.IN_PROGRESS].includes(
      this.status()!,
    ),
  );
  protected isCompleted = computed(() => RouteHelper.isCompleted(this.point()));

  protected serviceTime = computed(() => {
    const value = this.value();
    return 'serviceTime' in value ? (value.serviceTime ?? null) : null;
  });
  protected elapsedEnd = computed(() =>
    RouteHelper.getCompletedAt(this.point()),
  );
  protected elapsed = computed(() => {
    const arrivedAt = this.timestamps()?.arrivedAt;
    if (!arrivedAt) return null;

    const from = DateTime.fromISO(arrivedAt);
    if (this.isInPoint()) return this.commonHelper.nowMinute().diff(from);

    const to = this.elapsedEnd();
    if (!to) return null;

    return DateTime.fromISO(to).diff(from);
  });
  protected isOvertime = computed(() => {
    const serviceTime = this.serviceTime();
    const elapsed = this.elapsed();
    if (serviceTime == null || !elapsed) return false;

    return elapsed.as('seconds') > serviceTime;
  });

  protected color = computed<OnyxColor | null>(() => {
    if (this.isToDo() || this.isCanceled()) return null;
    if (this.isOvertime()) return 'red';
    if (this.isInPoint()) return 'blue';

    return 'green';
  });
  protected dotColor = computed<OnyxColor>(() =>
    this.isToDo() || this.isCanceled()
      ? 'light-black'
      : (this.color() ?? 'blue'),
  );
}
